// IndexedDB 数据层: 文件列表缓存 + 缩略图缓存 + 断点续传队列
// 用 idb 库包装, 简单可靠
import { openDB, DBSchema, IDBPDatabase } from 'idb';

export interface FileItem {
  uuid: string;
  displayName: string;
  originalName: string;
  mime: string;
  size: number;
  uploadedAt: number;
  takenAt?: number;
  width?: number;
  height?: number;
  duration?: number;
  hash?: string;
  starred?: boolean;
}

// 断点续传队列项: 用 Blob 存文件, 分片进度存数组
export interface QueuedUpload {
  id: string;                     // uuid, 即 R2 key
  uploadId?: string;              // R2 multipart uploadId (线上)
  fileBlob: Blob;                 // 原始文件 (IndexedDB 可存 Blob)
  filename: string;
  size: number;
  mime: string;
  totalParts: number;
  completedParts: number[];       // 已成功分片号
  status: 'queued' | 'uploading' | 'paused' | 'failed' | 'done';
  retries: number;
  createdAt: number;
  pausedReason?: 'offline' | 'background' | 'user' | 'refresh';
  takenAt?: number;
  width?: number;
  height?: number;
  duration?: number;
}

interface VaultDB extends DBSchema {
  files: {
    key: string;
    value: FileItem;
  };
  thumbs: {
    key: string;
    value: { blob: Blob; updatedAt: number };
  };
  upload_queue: {
    key: string;
    value: QueuedUpload;
    indexes: { 'by-status': string };
  };
}

let dbPromise: Promise<IDBPDatabase<VaultDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<VaultDB>> {
  if (!dbPromise) {
    dbPromise = openDB<VaultDB>('selfie-vault-db', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('files')) db.createObjectStore('files');
        if (!db.objectStoreNames.contains('thumbs')) db.createObjectStore('thumbs');
        if (!db.objectStoreNames.contains('upload_queue')) {
          const s = db.createObjectStore('upload_queue', { keyPath: 'id' });
          s.createIndex('by-status', 'status');
        }
      },
    });
  }
  return dbPromise;
}

// ---- files 缓存 ----
export async function cacheFiles(items: FileItem[]) {
  const db = await getDB();
  const tx = db.transaction('files', 'readwrite');
  await Promise.all([
    tx.store.clear(),
    ...items.map((it) => tx.store.put(it, it.uuid)),
  ]);
  await tx.done;
}
export async function getCachedFiles(): Promise<FileItem[]> {
  const db = await getDB();
  return db.getAll('files');
}

// ---- thumbs 缓存 ----
export async function cacheThumb(uuid: string, blob: Blob) {
  const db = await getDB();
  await db.put('thumbs', { blob, updatedAt: Date.now() }, uuid);
}
export async function getCachedThumb(uuid: string): Promise<Blob | undefined> {
  const db = await getDB();
  const r = await db.get('thumbs', uuid);
  return r?.blob;
}

// ---- upload_queue ----
export async function queueUpload(item: QueuedUpload) {
  const db = await getDB();
  await db.put('upload_queue', item);
}
export async function getQueued(uuid: string | undefined, status?: string): Promise<QueuedUpload | undefined> {
  const db = await getDB();
  if (status && uuid === undefined) return undefined;
  if (uuid) return db.get('upload_queue', uuid);
  const all = await db.getAll('upload_queue');
  return all[0];
}
export async function getAllQueued(status?: string): Promise<QueuedUpload[]> {
  const db = await getDB();
  if (status) {
    return db.getAllFromIndex('upload_queue', 'by-status', status);
  }
  return db.getAll('upload_queue');
}
export async function updateQueued(uuid: string, patch: Partial<QueuedUpload>) {
  const db = await getDB();
  const cur = await db.get('upload_queue', uuid);
  if (cur) await db.put('upload_queue', { ...cur, ...patch });
}
export async function deleteQueued(uuid: string) {
  const db = await getDB();
  await db.delete('upload_queue', uuid);
}
export async function clearDoneQueue() {
  const db = await getDB();
  const all = await db.getAll('upload_queue');
  const tx = db.transaction('upload_queue', 'readwrite');
  await Promise.all(
    all.filter((q) => q.status === 'done' || q.status === 'failed').map((q) => tx.store.delete(q.id))
  );
  await tx.done;
}